import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Maximize, BedDouble, Search, ArrowUpRight, Home as HomeIcon, Map as MapIcon, LayoutGrid, HeartPulse } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { AdBanner } from "../components/AdBanner";
import MapView from "../components/MapView";
import api from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const FILTERS = [
  { id: "", label: "Toutes" },
  { id: "vente", label: "À vendre" },
  { id: "location", label: "À louer" },
];

function ListingCard({ l }) {
  return (
    <Link
      to={`/annonces/${l.id}`}
      data-testid={`listing-card-${l.reference}`}
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-navy-surface/60 transition-colors hover:border-gold/40"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={l.images?.[0]} alt={l.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
        <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold ${l.transaction === "vente" ? "bg-gold text-navy" : "bg-white/90 text-navy"}`}>
          {l.transaction === "vente" ? "À vendre" : "À louer"}
        </span>
        <span className="absolute right-4 top-4 rounded-full bg-navy/70 px-3 py-1 text-xs text-slate-200 backdrop-blur-sm">{l.property_type}</span>
        {l.speed_detresse && (
          <span data-testid="card-carnet-badge" className="absolute bottom-4 left-4 flex items-center gap-1.5 rounded-full bg-gold/90 px-3 py-1 text-xs font-semibold text-navy">
            <HeartPulse className="h-3.5 w-3.5" /> Carnet de Santé
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="font-serif text-2xl text-gold">
          {fmt(l.price)}{l.transaction === "location" && <span className="text-base text-slate-400"> /mois</span>}
        </p>
        <h3 className="mt-1 font-serif text-xl text-white">{l.title}</h3>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-400"><MapPin className="h-3.5 w-3.5" /> {l.city}</p>
        <div className="mt-4 flex flex-wrap gap-4 border-t border-white/5 pt-4 text-sm text-slate-300">
          <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4 text-gold" /> {l.surface} m²</span>
          <span className="flex items-center gap-1.5"><HomeIcon className="h-4 w-4 text-gold" /> {l.rooms} p.</span>
          {l.bedrooms > 0 && <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-gold" /> {l.bedrooms} ch.</span>}
        </div>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-gold">
          Voir le bien <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}

export default function Annonces() {
  const [transaction, setTransaction] = useState("");
  const [city, setCity] = useState("");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    api.listings({ ...(transaction && { transaction }), ...(city && { city }) })
      .then((d) => { if (!ignore) setListings(d.listings); })
      .catch(() => {})
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [transaction, city]);

  return (
    <div data-testid="annonces-page">
      <Seo title="Annonces immobilières à Amiens" path="/annonces" description="Ventes et locations sélectionnées par Speed Détresse à Amiens. Carte interactive, fiches détaillées et demande de visite." />
      <PageHero
        overline="Annonces immobilières · Amiens"
        title={["Trouvez votre", "prochain bien"]}
        subtitle="Ventes et locations sélectionnées par Speed Détresse. Demandez une visite en un clic — chaque demande devient un dossier suivi."
        testid="annonces-hero"
      />

      {/* Filters */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2" data-testid="annonces-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id || "all"}
                data-testid={`filter-${f.id || "all"}`}
                onClick={() => setTransaction(f.id)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${transaction === f.id ? "bg-gold text-navy" : "border border-white/15 text-slate-300 hover:border-gold/50"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="annonces-city-search"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Rechercher une ville"
              className="w-full rounded-full border border-white/10 bg-navy px-11 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </div>
        </div>

        {/* View toggle */}
        <div className="mt-5 flex justify-end">
          <div className="inline-flex rounded-full border border-white/15 p-1">
            <button data-testid="view-grid" onClick={() => setView("grid")}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${view === "grid" ? "bg-gold text-navy" : "text-slate-300 hover:text-gold"}`}>
              <LayoutGrid className="h-3.5 w-3.5" /> Grille
            </button>
            <button data-testid="view-map" onClick={() => setView("map")}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${view === "map" ? "bg-gold text-navy" : "text-slate-300 hover:text-gold"}`}>
              <MapIcon className="h-3.5 w-3.5" /> Carte
            </button>
          </div>
        </div>
      </section>

      {/* Grid + ads */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-12 lg:px-8">
        <AdBanner index={0} className="mb-8" />
        {loading ? (
          <p className="py-20 text-center text-slate-500">Chargement des annonces…</p>
        ) : listings.length === 0 ? (
          <p className="py-20 text-center text-slate-500" data-testid="annonces-empty">Aucune annonce ne correspond à votre recherche.</p>
        ) : view === "map" ? (
          <div data-testid="annonces-map"><MapView listings={listings} height={560} /></div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {listings.map((l, i) => (
              <div key={l.id} className="contents">
                <Reveal delay={(i % 3) * 0.06}><ListingCard l={l} /></Reveal>
                {i === 2 && <Reveal className="sm:col-span-2 lg:col-span-3"><AdBanner index={1} /></Reveal>}
              </div>
            ))}
          </div>
        )}
        <AdBanner index={2} className="mt-8" />
        <Reveal className="mt-10">
          <p className="mx-auto max-w-3xl rounded-2xl border border-white/10 bg-navy p-5 text-center text-sm text-slate-400" data-testid="annonces-disclaimer">
            Speed Détresse fournit un espace de présentation et de mise en relation entre propriétaires abonnés.
            Speed Détresse ne prend parti ni pour l'acheteur ni pour le vendeur, ne perçoit aucune somme et ne réalise pas la transaction :
            les parties échangent directement et le notaire gère la vente comme d'habitude.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
